import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";
import type { LockedPayoutAccount } from "../transactions/endpoints";

/** One row of /users/access-table: the marketplace user joined with their
 * admin access, seller_info and Telegram/WhatsApp row. */
export interface AdminUserRow {
  admin_user_id?: string | null;
  user_id?: string | null;
  email: string;
  display_name: string | null;
  is_seller?: boolean | null;
  role: string | null;
  permissions: string[];
  /** From seller_info; kept when a seller is turned off */
  tariff_rate?: number | null;
  whatsapp_number?: string | null;
  telegram_linked?: boolean;
  created_at: string;
}

export interface UserCounts {
  total: number;
  sellers: number;
  buyers: number;
}

export interface UserAddress {
  address_id: string;
  first_name: string | null;
  last_name: string | null;
  address: string;
  postal_code: string;
  country: string;
  is_primary: boolean;
}

export interface UserWithAddresses {
  user: { user_id: string; username: string | null; email: string };
  addresses: UserAddress[];
}

const userPath = (userId: string) => `${ENDPOINTS.USERS}/${encodeURIComponent(userId.trim())}`;

export const getUsersAccessTable = (token: string) => request(token).get<AdminUserRow[]>(`${ENDPOINTS.USERS}/access-table`);

export const getUserCounts = (token: string) => request(token).get<UserCounts>(`${ENDPOINTS.USERS}/count`);

export const getUserAddresses = (userId: string, token: string) =>
  request(token).get<UserWithAddresses>(`${userPath(userId)}/addresses`);

export const updateTariffRate = (userId: string, tariffRate: number, token: string) =>
  request(token).put<{ tariff_rate?: number | null }>(`${userPath(userId)}/tariff-rate`, { tariff_rate: tariffRate });

export const updateWhatsappNumber = (userId: string, whatsappNumber: string | null, token: string) =>
  request(token).put<{ whatsapp_number?: string | null }>(`${userPath(userId)}/whatsapp-number`, {
    whatsapp_number: whatsappNumber,
  });

export const updateSellerStatus = (userId: string, isSeller: boolean, token: string) =>
  request(token).put<{ is_seller: boolean; tariff_rate: number | null }>(`${userPath(userId)}/seller-status`, {
    is_seller: isSeller,
  });

/** Permanently deletes a buyer account (the backend refuses accounts with records). */
export const deleteUserAccount = (userId: string, token: string) => request(token).delete(userPath(userId));

/** A seller's payout details, still encrypted; null when none are saved. */
export const getUserPayoutAccount = (userId: string, token: string) =>
  request(token).get<{ payout_account: LockedPayoutAccount | null }>(`${userPath(userId)}/payout-account`);
